import { InfraredFlight, MergedFlight } from "@nova/protobuf"
import { PluginSpec } from "plugins/plugin-registry"
import { InspectorPanelPlugin } from "../useInspectorPanel"
import {
    getGenericInspectorPanelComponent,
    getGenericLayerDataInspectorPanelComponent,
} from "../inspector-panels/GenericInspectorPanel"
import { InspectorPanelHotspotLayer } from "./InspectorPanelHotspotLayer"
import { InspectorPanelHotspotData } from "./InspectorPanelHotspotData"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import {
    selectHotspotMultiSelectFlight,
    selectMultiSelectHotspots,
} from "features/data-management/hotspots/selectors"
import { selectIsOrthoComparisonActive } from "plugins/Orthomosaic/OrthoComparisonSlice"
import { InspectorPanelOrthoComparisonPanel } from "../orthos/InspectorPanelOrthoComparisonPanel"
import { GenericInspectorPanelHeaderTopRow } from "../inspector-panels/headers/GenericInspectorPanelHeader"
import * as Icons from "@nova/component-icons"
import { ButtonDestructive, ButtonSecondary } from "@nova/component-btn"
import { WorkspaceConfirmationModalName } from "@nova/constants"
import { setWorkspaceConfirmationModalContext } from "features/content-overlay/slice"
import { closeHotspotMultiRemoval } from "features/data-management/hotspots/slice"
import { showGlobalModal } from "runlevel/actions"
import { InspectorPanelMergedFlightLayer } from "./mergedFlight.ts/InspectorPanelMergedFlightLayer"
import { InspectorPanelMergedFlightData } from "./mergedFlight.ts/InspectorPanelMergedFlightData"
import { OrthoMaskPanel } from "../orthos/OrthoMaskPanel"
import { selectOrthoMaskEditingFlightId } from "plugins/Orthomosaic/OrthoMaskSlice"
import { selectMergedFlightById } from "features/entity-caching/mergedFlights/selectors"
import { pluralize } from "@nova/util-strings"

function HotspotRemovalHeader() {
    return (
        <div className="flex flex-col px-4 pt-4 pb-2">
            <GenericInspectorPanelHeaderTopRow
                typeTitle="Remove Hotspots"
                Icon={Icons.HotspotAddOn}
            />
        </div>
    )
}

function HotspotRemovalBody() {
    const dispatch = useAppDispatch()
    const selectedHotspots = useAppSelector(selectMultiSelectHotspots)
    const onCancelClick = () => {
        dispatch(closeHotspotMultiRemoval())
    }

    const onRemovalClick = () => {
        dispatch(
            setWorkspaceConfirmationModalContext({
                entityIds: selectedHotspots.map((h) => h.id),
                type: WorkspaceConfirmationModalName.DeleteMultipleHotspots,
                entityName: `${selectedHotspots.length}`,
                entityId: WorkspaceConfirmationModalName.DeleteMultipleHotspots,
            })
        )
        dispatch(showGlobalModal())
    }

    return (
        <div className="flex flex-col gap-2 p-4">
            <h5 className="text-gray-300 select-none mb-2">
                Select an area to highlight hotspots.
            </h5>
            <ButtonDestructive
                onClick={onRemovalClick}
                disabled={!selectedHotspots.length}
                LeftIcon={Icons.Trash}
                title={`Remove ${pluralize({ count: selectedHotspots.length, str: "hotspot" })}`}
                className="w-full"
            />
            <ButtonSecondary title="Cancel" onClick={onCancelClick} className="w-full" />
        </div>
    )
}

export function getHotspotInspectorPanel(spec: PluginSpec<InfraredFlight>) {
    const LayerDataInspectorPanel = getGenericLayerDataInspectorPanelComponent({
        spec,
        LayerComponent: InspectorPanelHotspotLayer,
        DataComponent: InspectorPanelHotspotData,
        isScrollable: true,
    })
    const HotspotRemovalInspectorPanel = getGenericInspectorPanelComponent({
        spec,
        Header: HotspotRemovalHeader,
        Body: HotspotRemovalBody,
    })
    const HotspotInspectorPanel = ({
        id,
        plugin,
    }: {
        id?: string
        plugin: InspectorPanelPlugin
    }) => {
        const hotspotMultiSelectFlight = useAppSelector(selectHotspotMultiSelectFlight)
        const comparisonIsActive = useAppSelector(selectIsOrthoComparisonActive(id ?? ""))
        const editingFlightId = useAppSelector(selectOrthoMaskEditingFlightId)
        if (!id) return null
        if (comparisonIsActive) return <InspectorPanelOrthoComparisonPanel />
        if (editingFlightId === id) return <OrthoMaskPanel flightId={id} />
        if (hotspotMultiSelectFlight !== id) {
            return <LayerDataInspectorPanel id={id} plugin={plugin} />
        }
        return <HotspotRemovalInspectorPanel id={id} plugin={plugin} />
    }
    return HotspotInspectorPanel
}

export function getMergedHotspotInspectorPanel(spec: PluginSpec<MergedFlight>) {
    const LayerDataInspectorPanel = getGenericLayerDataInspectorPanelComponent({
        spec,
        LayerComponent: InspectorPanelMergedFlightLayer,
        DataComponent: InspectorPanelMergedFlightData,
        isScrollable: true,
    })
    const HotspotRemovalInspectorPanel = getGenericInspectorPanelComponent({
        spec,
        Header: HotspotRemovalHeader,
        Body: HotspotRemovalBody,
    })
    const MergedHotspotInspectorPanel = ({
        id,
        plugin,
    }: {
        id?: string
        plugin: InspectorPanelPlugin
    }) => {
        const hotspotMultiSelectFlight = useAppSelector(selectHotspotMultiSelectFlight)
        const editingFlightId = useAppSelector(selectOrthoMaskEditingFlightId)
        const mergedFlight = useAppSelector(selectMergedFlightById(id ?? ""))
        if (!id || !mergedFlight) return null
        if (editingFlightId === id) {
            return <OrthoMaskPanel flightId={id} />
        }
        if (
            hotspotMultiSelectFlight !== id &&
            !mergedFlight.hotspotFlightIds.some((childId) => hotspotMultiSelectFlight === childId)
        ) {
            return <LayerDataInspectorPanel id={id} plugin={plugin} />
        }
        return <HotspotRemovalInspectorPanel id={id} plugin={plugin} />
    }
    return MergedHotspotInspectorPanel
}
